import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
} from "@material-ui/core";
import { useSwitchOrgao, useUserOrgaos } from "../../../../../../services/api-tansquery/user/user-orgaos";
import { useAuth } from "../../../../../../contexts/auth";
import { useOrgao } from "../../../../../../contexts/orgao";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

interface SwitchOrgaoModalProps {
  open: boolean;
  onClose: () => void;
}

export default function SwitchOrgaoModal({ open, onClose }: SwitchOrgaoModalProps) {
  const { adminUser } = useAuth();
  const { data: userOrgaosData, isLoading } = useUserOrgaos();
  const switchOrgaoMutation = useSwitchOrgao();
  const { currentOrgaoId, setCurrentOrgaoId } = useOrgao();
  const queryClient = useQueryClient();

  const userOrgaosFromAPI = userOrgaosData?.user_orgaos || [];
  const userOrgaosFromAdminUser = adminUser?.orgaos?.map(item => ({
    id: item.orgao.id,
    nome: item.orgao.nome,
    sigla: item.orgao.sigla,
    perfil: item.perfil
  })) || [];
  
  const userOrgaos = userOrgaosFromAPI.length > 0 ? userOrgaosFromAPI : userOrgaosFromAdminUser;

  const handleSwitchOrgao = (orgaoId: number) => {
    if (orgaoId === currentOrgaoId) {
      toast.info("Você já está neste órgão.");
      onClose();
      return;
    }

    switchOrgaoMutation.mutate(orgaoId, {
      onSuccess: (data) => {
        setCurrentOrgaoId(orgaoId);
        
        // Invalidar todas as queries relacionadas para forçar refetch
        queryClient.invalidateQueries({ queryKey: ['attendance-local'] });
        queryClient.invalidateQueries({ queryKey: ['totem'] });
        queryClient.invalidateQueries({ queryKey: ['cancellation'] });
        queryClient.invalidateQueries({ queryKey: ['rescheduling'] });
        queryClient.invalidateQueries({ queryKey: ['admin-user'] });
        queryClient.invalidateQueries({ queryKey: ['user-orgaos'] });
        
        toast.success(`Órgão alterado para: ${data.orgao.sigla} - ${data.orgao.nome}`);
        onClose();
      },
      onError: (error: any) => {
        console.error('Erro ao trocar órgão:', error);
        const errorMessage = error?.response?.data?.error || 'Erro ao alterar órgão. Tente novamente.';
        toast.error(errorMessage);
      }
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Trocar Órgão</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : userOrgaos.length === 0 ? (
          <Typography color="textSecondary">
            Nenhum órgão disponível para troca.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Selecione um órgão para trocar:
            </Typography>
            <List>
              {userOrgaos.map((orgao) => {
                const isCurrent = orgao.id === (currentOrgaoId ?? null);
                return (
                  <ListItem
                    key={orgao.id}
                    button
                    onClick={() => handleSwitchOrgao(orgao.id)}
                    disabled={switchOrgaoMutation.isLoading || isCurrent}
                    selected={isCurrent}
                    style={{
                      backgroundColor: isCurrent ? '#f3f6fa' : 'transparent',
                      cursor: isCurrent ? 'default' : 'pointer',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1">
                            {orgao.sigla} - {orgao.nome}
                          </Typography>
                          {isCurrent && (
                            <Typography variant="caption" color="primary" style={{ fontWeight: 'bold', marginLeft: 8 }}>
                              (Atual)
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={orgao.perfil}
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={switchOrgaoMutation.isLoading}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

